import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faBan } from '@fortawesome/pro-light-svg-icons/faBan';
import { OUTREACH_FATIGUE_CAP_PER_WEEK } from 'fym-shared/src/music24/outreach/networkRules';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { ContactPoints } from '../components/ContactPoints';
import { SavedTextarea } from '../components/SavedTextarea';
import { TagEditor } from '../components/TagEditor';
import { useContactActions } from '../hooks/useContactActions';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import type { ContactDetail } from '../outreachTypes';
import { contactKindLabel, contactStatusLabel } from '../outreachLabels';
import { nameInitials, shortDate } from '../outreachFormat';
import { outreachMessages } from '../outreachMessages';
import { contactsMessages as messages } from './contactsMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Contacts.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ContactDrawerBodyProps {
  accountId: number;
  detail: ContactDetail;
  onClose: () => void;
}

/**
 * One contact (design outreach q2): the points with their source, the targets, the pitch history,
 * the workspace notes and tags and "Do not contact" (this workspace only).
 */
const ContactDrawerBodyComponent: React.FC<ContactDrawerBodyProps> = ({ accountId, detail, onClose }) => {
  const intl = useIntl();
  const plan = useOutreachPlan();
  const { updateContact, isUpdating } = useContactActions(accountId);
  const uuid = detail.workspaceContactUuid;
  const isBlocked = detail.status === 'doNotContact';
  // The curator stopped this artist on the stop page: the workspace cannot allow pitches again.
  const { stoppedByRecipient } = detail;

  const targets = useMemo(
    () =>
      detail.targets.map((target) => (
        <div key={target.targetUuid} className={shared.miniRow}>
          <div>
            <b>{target.name}</b>
            <small>{intl.formatMessage(outreachMessages.followers, { count: target.followers ?? 0 })}</small>
          </div>
        </div>
      )),
    [detail.targets, intl]
  );
  const history = useMemo(
    () =>
      detail.history.map((row) => (
        <div key={row.campaignUuid} className={shared.miniRow}>
          <div className={shared.grow}>
            <b>{row.campaignName}</b>
            <small>{intl.formatMessage(messages.historyRow, { date: shortDate(intl, row.sentAt) })}</small>
          </div>
        </div>
      )),
    [detail.history, intl]
  );

  const toggleBlock = useCallback(() => {
    // eslint-disable-next-line no-alert -- a short confirm before the stop
    if (!isBlocked && !window.confirm(intl.formatMessage(messages.doNotContactConfirm, { name: detail.name }))) {
      return;
    }
    updateContact({ workspaceContactUuid: uuid, status: isBlocked ? 'new' : 'doNotContact' }).catch(console.error);
  }, [detail.name, intl, isBlocked, updateContact, uuid]);

  return (
    <>
      <div className={shared.drawerHead}>
        <span className={styles.avatar}>{nameInitials(detail.name)}</span>
        <div className={shared.grow}>
          <span className={shared.eyebrow}>
            {intl.formatMessage(contactKindLabel(detail.imported ? 'private' : detail.kind))}
          </span>
          <h3 className={`${shared.h2} mt-1`}>{detail.name}</h3>
          <span className={shared.chip}>{intl.formatMessage(contactStatusLabel(detail.status))}</span>
        </div>
        <button
          type="button"
          className="btn btn-link text-reset"
          aria-label={intl.formatMessage(outreachMessages.close)}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      <div>
        <span className={`${shared.eyebrow} mb-2`}>{intl.formatMessage(messages.pointsTitle)}</span>
        {detail.points.length === 0 && detail.email !== null ? (
          <div className={shared.pointRow}>
            <b>{detail.email}</b>
          </div>
        ) : (
          <ContactPoints accountId={accountId} contactUuid={detail.contactUuid} points={detail.points} />
        )}
        {!detail.hasEmail && <p className={`${shared.small} mt-2`}>{intl.formatMessage(messages.noEmailNote)}</p>}
      </div>
      {targets.length > 0 && (
        <div>
          <span className={`${shared.eyebrow} mb-2`}>{intl.formatMessage(messages.targetsTitle)}</span>
          <div className={shared.miniList}>{targets}</div>
        </div>
      )}
      <div>
        <span className={`${shared.eyebrow} mb-2`}>{intl.formatMessage(messages.historyTitle)}</span>
        <div className={shared.miniList}>
          {history.length === 0 ? (
            <span className={shared.small}>{intl.formatMessage(messages.historyNone)}</span>
          ) : (
            history
          )}
        </div>
        <p className={`${shared.small} mt-2`}>
          {intl.formatMessage(messages.networkLine, { cap: OUTREACH_FATIGUE_CAP_PER_WEEK })}
        </p>
      </div>
      <div>
        <span className={`${shared.eyebrow} mb-2`}>{intl.formatMessage(messages.tagsTitle)}</span>
        <TagEditor
          values={detail.tags}
          onChange={(tags) => updateContact({ workspaceContactUuid: uuid, tags }).catch(console.error)}
          addLabel={intl.formatMessage(messages.addTag)}
          disabled={plan.isDemo}
        />
      </div>
      <SavedTextarea
        label={intl.formatMessage(messages.notesTitle)}
        value={detail.notes ?? ''}
        onSave={(notes) =>
          updateContact({ workspaceContactUuid: uuid, notes: notes.trim() === '' ? null : notes }).catch(console.error)
        }
        maxLength={2000}
        disabled={plan.isDemo}
      />
      <div className={shared.row}>
        <span className={shared.grow} />
        {!stoppedByRecipient && (
          <SMLButton
            outline
            color="danger"
            size="sm"
            omitCap
            leftIcon={faBan}
            onClick={toggleBlock}
            isLoading={isUpdating}
            disabled={plan.isDemo}
          >
            {intl.formatMessage(isBlocked ? messages.allowContact : messages.setDoNotContact)}
          </SMLButton>
        )}
      </div>
      {stoppedByRecipient && <p className={shared.small}>{intl.formatMessage(messages.stoppedByRecipient)}</p>}
      <p className={shared.small}>{withBrandWord(intl.formatMessage(messages.doNotContactNote))}</p>
    </>
  );
};

export const ContactDrawerBody = TypedMemo(ContactDrawerBodyComponent);
